/**
 * AppLayout Component - Application Shell
 * Requirements: 8.1, 8.2, 8.5, 8.6 - Application shell with header, sidebar, main content
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../../features/auth/context/AuthContext';
import { Sidebar, DRAWER_WIDTH } from './Sidebar';
import { AppBreadcrumbs, type BreadcrumbItem } from './Breadcrumbs';

// 横向き検出用カスタムフック
const useIsLandscape = () => {
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight
  );

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return isLandscape;
};

// Route to breadcrumb/title mapping
const ROUTE_CONFIG: Record<string, { title: string; breadcrumbs: BreadcrumbItem[] }> = {
  '/dashboard': {
    title: 'ダッシュボード',
    breadcrumbs: [],
  },
  '/account': {
    title: 'アカウント情報',
    breadcrumbs: [{ label: 'アカウント情報' }],
  },
  '/questionnaire': {
    title: '質問回答',
    breadcrumbs: [{ label: '質問回答' }],
  },
  '/analysis/my-results': {
    title: '自分の分析結果',
    breadcrumbs: [
      { label: '分析結果', path: '/analysis' },
      { label: '自分の分析結果' },
    ],
  },
  '/analysis/department-results': {
    title: '部門内分析結果一覧',
    breadcrumbs: [
      { label: '分析結果', path: '/analysis' },
      { label: '部門内分析結果一覧' },
    ],
  },
  '/analysis/company-results': {
    title: '全社分析結果一覧',
    breadcrumbs: [
      { label: '分析結果', path: '/analysis' },
      { label: '全社分析結果一覧' },
    ],
  },
  '/aggregation/department/distribution': {
    title: '部門内思考傾向分布',
    breadcrumbs: [
      { label: '集計', path: '/aggregation' },
      { label: '部門内思考傾向分布' },
    ],
  },
  '/aggregation/department/network': {
    title: '部門内ネットワーク図',
    breadcrumbs: [
      { label: '集計', path: '/aggregation' },
      { label: '部門内ネットワーク図' },
    ],
  },
  '/aggregation/company/distribution': {
    title: '全社思考傾向分布',
    breadcrumbs: [
      { label: '集計', path: '/aggregation' },
      { label: '全社思考傾向分布' },
    ],
  },
  '/aggregation/company/network': {
    title: '組織分析',
    breadcrumbs: [
      { label: '集計', path: '/aggregation' },
      { label: '組織分析' },
    ],
  },
  '/admin/question-sets': {
    title: '質問セット管理',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: '質問セット管理' },
    ],
  },
  '/admin/organization': {
    title: '組織管理',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: '組織管理' },
    ],
  },
  '/admin/bulk-registration': {
    title: 'ユーザー一括登録',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: 'ユーザー一括登録' },
    ],
  },
  '/admin/users': {
    title: 'ユーザー管理',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: 'ユーザー管理' },
    ],
  },
  '/admin/judgment-patterns': {
    title: '判定パターン管理',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: '判定パターン管理' },
    ],
  },
  '/admin/team-judgment-patterns': {
    title: 'チーム判定パターン管理',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: 'チーム判定パターン管理' },
    ],
  },
  '/admin/analysis-results': {
    title: '分析結果管理',
    breadcrumbs: [
      { label: '管理', path: '/admin' },
      { label: '分析結果管理' },
    ],
  },
};

export const AppLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useIsLandscape();

  // SELF_ONLYユーザーはサイドメニューを非表示
  const isSelfOnly = user?.permissionLevel === 'SELF_ONLY';
  
  // SELF_ONLYユーザーのモバイル時（縦向き・横向き両方）はヘッダーとナビゲーションを非表示
  const hideHeaderForSelfOnly = isSelfOnly && isMobile;

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    await logout();
  };

  const routeConfig = useMemo(() => {
    return ROUTE_CONFIG[location.pathname] ?? {
      title: 'ページ',
      breadcrumbs: [],
    };
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Header - SELF_ONLYユーザーの横向き時は非表示 */}
      {!hideHeaderForSelfOnly && (
        <AppBar
          position="fixed"
          component="header"
          role="banner"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            {/* SELF_ONLYユーザーはハンバーガーメニューを非表示 */}
            {!isSelfOnly && (
              <IconButton
                color="inherit"
                aria-label="メニューを開く"
                edge="start"
                onClick={toggleSidebar}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              GB照らす マイページ
            </Typography>
            {isAuthenticated && user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {user.companyName ? `${user.companyName} 様` : user.email}
                </Typography>
                <Button
                  color="inherit"
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                  aria-label="ログアウト"
                >
                  ログアウト
                </Button>
              </Box>
            )}
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar - SELF_ONLYユーザーは非表示 */}
      {!isSelfOnly && (
        <Sidebar
          user={user}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isMobile={isMobile}
        />
      )}

      {/* Sidebar Spacer - persistent Drawerはflexに参加しないため、スペーサーで幅を確保 */}
      {!isSelfOnly && !isMobile && sidebarOpen && (
        <Box
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            transition: (theme) =>
              theme.transitions.create(['width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          }}
        />
      )}

      {/* Main Content */}
      <Box
        component="main"
        role="main"
        sx={{
          flexGrow: 1,
          p: hideHeaderForSelfOnly ? 0 : 3,
          mt: hideHeaderForSelfOnly ? 0 : 8, // AppBar height (64px) + some margin
          transition: (theme) =>
            theme.transitions.create(['margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        {/* Breadcrumbs - SELF_ONLYユーザーの横向き時は非表示 */}
        {!hideHeaderForSelfOnly && <AppBreadcrumbs items={routeConfig.breadcrumbs} />}

        {/* Page Title - SELF_ONLYユーザーの横向き時は非表示 */}
        {!hideHeaderForSelfOnly && (
          <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
            {routeConfig.title}
          </Typography>
        )}

        {/* Page Content */}
        <Outlet />
      </Box>
    </Box>
  );
};
